/**
 * Secure Markdown Renderer Component
 * 
 * P7 Security:
 * - NO rehype-raw plugin (prevents raw HTML injection)
 * - Content sanitisation before rendering
 * - XSS protection via component overrides
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitiseLLMProse, sanitiseLLMCode } from '../utils/sanitise';

// Default markdown styling classes
const defaultMdClasses = `prose prose-invert prose-sm max-w-none
  [&_h1]:text-lg [&_h1]:font-heading [&_h1]:text-[var(--hig-accent)] [&_h1]:mt-4 [&_h1]:mb-2
  [&_h2]:text-base [&_h2]:font-heading [&_h2]:text-[var(--hig-accent)] [&_h2]:mt-3 [&_h2]:mb-2
  [&_h3]:text-sm [&_h3]:font-heading [&_h3]:text-[var(--hig-accent)] [&_h3]:mt-2 [&_h3]:mb-1
  [&_p]:text-sm [&_p]:text-foreground/80 [&_p]:leading-relaxed
  [&_ul]:text-sm [&_ul]:text-foreground/80 [&_ol]:text-sm [&_ol]:text-foreground/80
  [&_li]:mb-1 [&_strong]:text-foreground
  [&_code]:text-[var(--hig-accent)] [&_code]:bg-[var(--hig-accent)]/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs
  [&_table]:border-collapse [&_table]:w-full [&_table]:text-xs
  [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:border [&_th]:border-[var(--n-border-2)] [&_th]:bg-[var(--n-bg)] [&_th]:text-[var(--hig-accent)] [&_th]:font-medium
  [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[var(--n-border-2)] [&_td]:text-foreground/80`;

/**
 * Custom link renderer that ensures safe href values
 */
const SafeLink = ({ href, children, ...props }) => {
  // Only allow http/https links
  const isSafe = href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/'));
  
  if (!isSafe) {
    // Render as plain text if href is suspicious
    return <span className="text-[var(--hig-accent)]">{children}</span>;
  }
  
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="text-[var(--hig-accent)] hover:underline"
      {...props}
    >
      {children}
    </a>
  );
};

/**
 * Custom code renderer with syntax highlighting placeholder
 */
const SafeCode = ({ inline, className, children, ...props }) => {
  const codeContent = String(children).replace(/\n$/, '');
  
  if (inline) {
    return (
      <code 
        className="text-[var(--hig-accent)] bg-[var(--hig-accent)]/10 px-1.5 py-0.5 rounded text-xs"
        {...props}
      >
        {codeContent}
      </code>
    );
  }
  
  // Block code - sanitise before display
  const sanitised = sanitiseLLMCode(codeContent);
  
  return (
    <pre className="bg-[var(--n-bg)] border border-[var(--n-border-2)] rounded-lg p-3 overflow-x-auto">
      <code className={className} {...props}>
        {sanitised}
      </code>
    </pre>
  );
};

/**
 * Secure Markdown component for rendering LLM output
 * 
 * @param {string} content - Markdown content to render
 * @param {string} className - Additional CSS classes
 * @param {boolean} sanitise - Whether to pre-sanitise content (default: true)
 */
export function SecureMarkdown({ content, className = '', sanitise = true }) {
  if (!content) return null;
  
  // Pre-sanitise content if requested
  const processedContent = sanitise ? sanitiseLLMProse(content) : content;
  
  return (
    <div className={`${defaultMdClasses} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // NO rehypePlugins - especially NOT rehype-raw
        components={{
          // Override link rendering for safety
          a: SafeLink,
          // Override code rendering for safety
          code: SafeCode,
          // Block potentially dangerous elements
          script: () => null,
          iframe: () => null,
          object: () => null,
          embed: () => null,
          form: () => null,
          input: () => null,
          button: () => null,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Markdown renderer for code/script output (no prose sanitisation)
 */
export function CodeMarkdown({ content, className = '' }) {
  if (!content) return null;
  
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: SafeLink,
          code: SafeCode,
          script: () => null,
          iframe: () => null,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default SecureMarkdown;
